import { CopyIcon, PencilSimpleIcon, PowerIcon, TrashIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetBookingTypesBuildings } from '@/api/booking-types'
import { Service, ServiceSku, useGetServices, useUpdateSku } from '@/api/services'
import ServiceIconSvg from '@/components/Icons'
import { LinkedColumn, LinkedColumns } from '@/components/layout/linked-columns'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'
import { useDesignVersionToggle } from '@/hooks'
import { selectBuildings } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import { getPlaceName } from '../places/places.utils'
import { CountBadgeTooltip } from '../shared/count-badge-tooltip'
import { ServiceFormModal } from './modals/service-form-modal'
import { SkuFormModal } from './modals/sku-form-modal'
import { SERVICE_CATEGORY_LIST } from './services.constants'
import { useServicesDelete } from './services.delete'
import { useServicesSelection } from './services.hooks'
import * as S from './services.styled'
import {
  formatCost,
  getBookingTypeRef,
  getCategoryLabel,
  getTrainingTypeStatus,
  isTimedCategory,
  sortServices,
  sortSkus,
} from './services.utils'

const EMPTY_SERVICES: Service[] = []

const COLUMN_LINKS = [
  { from: 'category', to: 'service' },
  { from: 'service', to: 'sku' },
]

const getServiceTitle = (service: Service) => service.name
const getSkuTitle = (sku: ServiceSku) => sku.name

export const ServicesPage = () => {
  const { t } = useTranslation(['organisation', 'common'])

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'services',
    screen: 'Services',
    to: { path: '/dashboard/organisation/services/legacy' },
    version: 'new',
  })

  const servicesQuery = useGetServices()
  const { mutate: updateSku } = useUpdateSku()
  const { handleDeleteService, handleDeleteSku } = useServicesDelete()
  const buildings = useSelector(selectBuildings)

  const services = servicesQuery.data ?? EMPTY_SERVICES

  const { selectCategory, selection, selectService, selectServiceIn, selectSku } =
    useServicesSelection(services)

  const buildingNames = useMemo(
    () => new Map(buildings.map((building) => [building.id, getPlaceName(building)])),
    [buildings],
  )

  const categoryServices = useMemo(
    () => sortServices(services.filter(({ category }) => category === selection.category)),
    [services, selection.category],
  )

  const selectedService = useMemo(
    () => services.find(({ id }) => id === selection.serviceId),
    [services, selection.serviceId],
  )

  const skus = useMemo(() => sortSkus(selectedService?.sku ?? []), [selectedService])

  const servicesPerCategory = useMemo(
    () =>
      services.reduce(
        (counts, { category }) => counts.set(category, (counts.get(category) ?? 0) + 1),
        new Map<string, number>(),
      ),
    [services],
  )

  const categoryItems = useMemo(
    () => SERVICE_CATEGORY_LIST.map((name, index) => ({ id: index, name })),
    [],
  )

  const bookingTypeIds = useMemo(
    () => [...new Set(categoryServices.flatMap((service) => getBookingTypeRef(service)?.id ?? []))],
    [categoryServices],
  )
  const buildingsByBookingType = useGetBookingTypesBuildings(bookingTypeIds)

  if (redirect) return redirect

  const handleAddService = () =>
    popupFactory.open(ServiceFormModal, {
      category: selection.category ?? undefined,
      onSaved: ({ category, id }) => selectServiceIn(category, id),
    })

  const handleEditService = (service: Service) =>
    popupFactory.open(ServiceFormModal, {
      onSaved: ({ category, id }) => selectServiceIn(category, id),
      service,
    })

  const handleDuplicateService = (service: Service) =>
    popupFactory.open(ServiceFormModal, {
      duplicate: true,
      onSaved: ({ category, id }) => selectServiceIn(category, id),
      service,
    })

  const handleAddSku = () => {
    if (!selectedService) return
    popupFactory.open(SkuFormModal, { onSaved: selectSku, service: selectedService })
  }

  const handleEditSku = (sku: ServiceSku) => {
    if (!selectedService) return
    popupFactory.open(SkuFormModal, { service: selectedService, sku })
  }

  const handleDuplicateSku = (sku: ServiceSku) => {
    if (!selectedService) return
    popupFactory.open(SkuFormModal, {
      duplicate: true,
      onSaved: selectSku,
      service: selectedService,
      sku,
    })
  }

  const handleToggleSku = (sku: ServiceSku) => {
    const activate = sku.active === false

    updateSku(
      { active: activate, id: sku.id },
      {
        onError: (error) => toastFactory.error(getApiErrorMessage(error)),
        onSuccess: () =>
          toastFactory.success(
            t(
              activate
                ? 'organisation:servicesPage.sku.toasts.activated'
                : 'organisation:servicesPage.sku.toasts.deactivated',
            ),
          ),
      },
    )
  }

  const handleSelectSku = (skuId: number) => {
    selectSku(skuId)
    const sku = skus.find(({ id }) => id === skuId)
    if (sku) handleEditSku(sku)
  }

  const getServiceSubtitle = (service: Service) =>
    [
      t('organisation:servicesPage.columns.services.subtitle', { count: service.sku.length }),
      !service.active
        ? t('organisation:servicesPage.columns.services.inactive')
        : !service.activeType
          ? t('organisation:servicesPage.columns.services.hiddenOnline')
          : undefined,
    ]
      .filter(Boolean)
      .join(' · ')

  const getServiceSubtitleEnd = (service: Service) => {
    const bookingType = getBookingTypeRef(service)
    if (!bookingType) return null

    const typeBuildings = buildingsByBookingType.get(bookingType.id) ?? []
    if (typeBuildings.length === 0) {
      return (
        <Typography as="span" color="black500" size="text-small">
          {bookingType.name}
        </Typography>
      )
    }

    return (
      <CountBadgeTooltip
        scrollable
        badgeLabel={bookingType.name}
        items={typeBuildings
          .map(({ id, name }) => buildingNames.get(id) ?? name ?? String(id))
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))}
        limit={typeBuildings.length}
        variant="text"
      />
    )
  }

  const getSkuSubtitle = (sku: ServiceSku) =>
    [
      sku.duration ? `${sku.duration} min` : undefined,
      formatCost(Number(sku.cost)),
      sku.quantity && sku.quantity > 1
        ? t('organisation:servicesPage.columns.skus.quantity', { count: sku.quantity })
        : undefined,
      sku.active === false ? t('organisation:servicesPage.columns.skus.inactive') : undefined,
    ]
      .filter(Boolean)
      .join(' · ')

  const getSkuSubtitleEnd = (sku: ServiceSku) => {
    if (!selectedService || !isTimedCategory(selectedService.category)) return null

    const status = getTrainingTypeStatus(selectedService, sku.duration)
    if (status.created || status.reason !== 'missingVisuals') return null

    return (
      <Tooltip
        trigger={
          <Typography as="span" color="red500" size="text-small">
            {t('organisation:servicesPage.columns.skus.noClassType')}
          </Typography>
        }
      >
        {t('organisation:servicesPage.columns.skus.noClassTypeHint')}
      </Tooltip>
    )
  }

  const editItem = (onClick: VoidFunction) => ({
    icon: PencilSimpleIcon,
    label: t('common:actions.edit'),
    onClick,
  })

  const duplicateItem = (onClick: VoidFunction) => ({
    icon: CopyIcon,
    label: t('organisation:servicesPage.actions.duplicate'),
    onClick,
  })

  const deleteItem = (onClick: VoidFunction) => ({
    icon: TrashIcon,
    label: t('common:actions.delete'),
    onClick,
    variant: 'danger' as const,
  })

  const error = servicesQuery.error

  return (
    <Page centered>
      <PageHeader
        title={t('organisation:servicesPage.title')}
        actions={
          <ToggleSwitch
            checked
            falsyLabel={t('organisation:servicesPage.designToggle.old')}
            truthyLabel={t('organisation:servicesPage.designToggle.new')}
            onChange={handleUIToggle}
          />
        }
      />

      {error ? (
        <EmptyState
          description={getApiErrorMessage(error)}
          title={t('organisation:servicesPage.error')}
        />
      ) : (
        <LinkedColumns links={COLUMN_LINKS}>
          <LinkedColumn
            dndType="category"
            emptyDescription=""
            emptyTitle=""
            getSubtitle={({ name }) =>
              t('organisation:servicesPage.columns.categories.subtitle', {
                count: servicesPerCategory.get(name) ?? 0,
              })
            }
            getTitle={({ name }) => getCategoryLabel(t, name)}
            items={categoryItems}
            loading={servicesQuery.isLoading}
            selectedId={categoryItems.find(({ name }) => name === selection.category)?.id ?? null}
            title={t('organisation:servicesPage.columns.categories.title')}
            onSelect={(id) => selectCategory(SERVICE_CATEGORY_LIST[id])}
          />

          <LinkedColumn
            addLabel={t('organisation:servicesPage.columns.services.add')}
            dndType="service"
            emptyDescription={t('organisation:servicesPage.columns.services.empty.description')}
            emptyTitle={t('organisation:servicesPage.columns.services.empty.title')}
            getLeading={(service) => (
              <S.ServiceIcon $color={service.color || '#999999'} $inactive={!service.active}>
                {service.icon && <ServiceIconSvg fill="currentColor" icon={service.icon} />}
              </S.ServiceIcon>
            )}
            getMenuItems={(service) => [
              editItem(() => handleEditService(service)),
              duplicateItem(() => handleDuplicateService(service)),
              deleteItem(() => handleDeleteService(service)),
            ]}
            getSubtitle={getServiceSubtitle}
            getSubtitleEnd={getServiceSubtitleEnd}
            getTitle={getServiceTitle}
            items={categoryServices}
            loading={servicesQuery.isLoading}
            selectedId={selection.serviceId}
            title={
              selection.category
                ? t('organisation:servicesPage.columns.services.titleIn', {
                    parent: getCategoryLabel(t, selection.category),
                  })
                : t('organisation:servicesPage.columns.services.title')
            }
            onAdd={handleAddService}
            onEdit={handleEditService}
            onSelect={selectService}
          />

          <LinkedColumn
            addLabel={t('organisation:servicesPage.columns.skus.add')}
            dndType="sku"
            emptyDescription={t('organisation:servicesPage.columns.skus.empty.description')}
            emptyTitle={t('organisation:servicesPage.columns.skus.empty.title')}
            getMenuItems={(sku) => [
              editItem(() => handleEditSku(sku)),
              duplicateItem(() => handleDuplicateSku(sku)),
              {
                icon: PowerIcon,
                label: t(
                  sku.active === false
                    ? 'organisation:servicesPage.actions.activate'
                    : 'organisation:servicesPage.actions.deactivate',
                ),
                onClick: () => handleToggleSku(sku),
              },
              deleteItem(() => handleDeleteSku(sku)),
            ]}
            getSubtitle={getSkuSubtitle}
            getSubtitleEnd={getSkuSubtitleEnd}
            getTitle={getSkuTitle}
            items={skus}
            loading={servicesQuery.isLoading}
            missingParent={
              selectedService
                ? undefined
                : {
                    description: t('organisation:servicesPage.columns.skus.noParent.description'),
                    hint: t('organisation:servicesPage.columns.skus.noParent.hint'),
                    title: t('organisation:servicesPage.columns.skus.noParent.title'),
                  }
            }
            selectedId={selection.skuId}
            title={
              selectedService
                ? t('organisation:servicesPage.columns.skus.titleIn', {
                    parent: selectedService.name,
                  })
                : t('organisation:servicesPage.columns.skus.title')
            }
            onAdd={selectedService ? handleAddSku : undefined}
            onEdit={handleEditSku}
            onSelect={handleSelectSku}
          />
        </LinkedColumns>
      )}
    </Page>
  )
}
